import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {symbol as Symbol} from '@wix/santa-editor-symbols';
import {TimePickerUtils} from 'wix-ui-core/time-picker';
import {shouldRenderSection} from '../../../utils/panelUtils';
import {
  Checkbox,
  Composites,
  Divider,
  DropDown,
  InfoIcon,
  RadioButtons,
  RichText,
  Slider,
  TextInput,
  TextLabel,
  Thumbnails,
  ToggleSwitch,
  Tooltip,
  TextButton,
  IconButton,
  SectionDivider,
  Text,
} from '@wix/wix-base-ui';
import Delete from 'wix-ui-icons-common/classic-editor/Delete';
import Add from 'wix-ui-icons-common/classic-editor/Add';
import {hoc} from '@wix/santa-editor-utils';
import {isValidRangeTimeString, isEndTimeAfterStartTime, isTimeIn24HourRange} from './utils';
import biEvents from '../bi/events';

/****** Time format (24-hour vs AM/PM) ******/
const TIME_FORMATS = [
  {
    value: true,
    label: 'time_input_settings_time_format_12H'
  },
  {
    value: false,
    label: 'time_input_settings_time_format_24H'
  }
];

function timeValidator(value) {
  return TimePickerUtils.isValidTime(value, false);
}

function formatValue(value) {
  if (timeValidator(value) && value.length === 5) {
    return `${value}:00.000`;
  } else {
    return value;
  }
}

function timeToDisplay(value) {
  if (timeValidator(value)) {
    return value.substr(0, 5);
  } else {
    return value;
  }
}

class TimePickerSettingsPanel extends Component {
    static displayName = 'TimePickerSettingsPanel'

    static propTypes = {
      compProperties: PropTypes.object.isRequired,
      compData: PropTypes.object.isRequired,
      updateProperties: PropTypes.func.isRequired,
      updateData: PropTypes.func.isRequired,
      experimentIsOpen: PropTypes.func.isRequired,
      panelSectionsDefinition: PropTypes.object,
      panelSections: PropTypes.object,
      translate: PropTypes.func.isRequired,
      openHelpArticle: PropTypes.func.isRequired,
      bi: PropTypes.object.isRequired,
    }

    shouldRemoveLabelToggle() {
      return this.props.experimentIsOpen('se_InputElementsRemoveLabelToggles');
    }

    state = {
      labelToggle: !!this.props.compData.label,
      currentLabel: this.shouldRemoveLabelToggle() ? this.props.compData.label :
        this.props.compData.label || this.props.translate('time_input_default_field_title'),
      partiallyFilledTimeSlots: {
        enabledTimes: {},
        disabledTimes: {}
      },
      lastModifiedTimeRange: {
        enabledTimes: {},
        disabledTimes: {},
      },
    }

    shouldRenderLabelSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.LABEL);
    shouldRenderAmPmFormatSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.USE_AM_PM_FORMAT);
    shouldRenderInputOptionSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INPUT_OPTION);
    shouldRenderInitialTimeSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INITIAL_TIME);
    shouldRenderInitialTextNothingSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INITIAL_TEXT_NOTHING);
    shouldRenderInitialTextPlaceholderSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INITIAL_TEXT_PLACEHOLDER);
    shouldRenderInitialTextCurrentTimeSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INITIAL_TEXT_CURRENT_TIME);
    shouldRenderInitialTextSpecificTimeSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.INITIAL_TEXT_SPECIFIC_TIME);
    shouldRenderStepSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.STEP);
    shouldRenderRequiredSection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.REQUIRED);
    shouldRenderReadOnlySection = () => shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.READ_ONLY);
    shouldRenderTimeSlotsSection = () => this.props.experimentIsOpen('se_TimePickerDisabledTimes') &&
    shouldRenderSection(this.props.panelSectionsDefinition, this.props.panelSections.DISABLED_TIMES)
    shouldRenderGeneralSettings = () => this.shouldRenderRequiredSection() || this.shouldRenderReadOnlySection()

    getEnabledTimes = () => {
      const partiallyFilledEnabledTimes = this.state.partiallyFilledTimeSlots.enabledTimes;
      const partiallyFilledEnabledTimeSlotsArray = Object.keys(partiallyFilledEnabledTimes)
        .filter(index => !this.props.compProperties.enabledTimes[index])
        .map(index => ({
          startTime: partiallyFilledEnabledTimes[index].startTime,
          endTime: partiallyFilledEnabledTimes[index].endTime,
        }));

      return (this.props.compProperties.enabledTimes || []).concat(partiallyFilledEnabledTimeSlotsArray);
    }

    getDisabledTimes = () => {
      const partiallyFilledDisabledTimes = this.state.partiallyFilledTimeSlots.disabledTimes;
      const partiallyFilledDisabledTimeSlotsArray = Object.keys(partiallyFilledDisabledTimes)
        .filter(index => !this.props.compProperties.disabledTimes[index])
        .map(index => ({
          startTime: partiallyFilledDisabledTimes[index].startTime,
          endTime: partiallyFilledDisabledTimes[index].endTime,
        }));

      return (this.props.compProperties.disabledTimes || []).concat(partiallyFilledDisabledTimeSlotsArray);
    }

    getGeneralSettingsTooltipContent = () => {
      if (!this.shouldRenderRequiredSection()) {
        return 'Time_Input_ReadOnlyOrNot_Tooltip_Configuration';
      }
      if (!this.shouldRenderReadOnlySection()) {
        return 'Time_Input_RequiredOrNot_Tooltip_Configuration';
      }
      return 'Text_Input_Tooltip_Configuration';
    }
    handleLabelToggleChanged = labelToggle => {
      const label = labelToggle ? this.state.currentLabel : '';
      this.setState({labelToggle});
      this.updateData('label', label);
    }

    handleLabelChanged = val => {
      this.props.bi.event(biEvents.changeValue, {
        inputType: 'timePicker',
        field_type: 'text input',
        field_name: 'title',
        previousValue: this.state.currentLabel,
        value: val,
      });

      this.setState({currentLabel: val});
      this.updateData('label', val);
    }

    onUseAmPmFormatChange = value => {
      this.props.bi.event(biEvents.changeValue, {
        inputType: 'timePicker',
        field_type: 'dropdown',
        field_name: 'Time format',
        previousValue: this.props.compProperties.useAmPmFormat,
        value,
      });

      this.props.updateProperties('useAmPmFormat', value);
    }

    getTimeRangeInvalidMessage = (timeSlot, modifiedTime, index, timeType) => {
      let invalidMessage = '';
      const updatedTimeSlot = {
        startTime: (this.state.partiallyFilledTimeSlots[timeType][index] && this.state.partiallyFilledTimeSlots[timeType][index].startTime) || timeSlot.startTime,
        endTime: (this.state.partiallyFilledTimeSlots[timeType][index] && this.state.partiallyFilledTimeSlots[timeType][index].endTime) || timeSlot.endTIme,
      };

      if (!updatedTimeSlot[modifiedTime]) {
        invalidMessage = '';
      } else if (!isTimeIn24HourRange(updatedTimeSlot[modifiedTime])) {
        invalidMessage = 'time_input_settings_invalid_time_format';
      } else if (!isValidRangeTimeString(updatedTimeSlot[modifiedTime])) {
        invalidMessage = 'time_input_settings_invalid_time_error';
      } else if (!isEndTimeAfterStartTime(updatedTimeSlot.startTime, updatedTimeSlot.endTime)) {
        invalidMessage = 'time_input_settings_invalid_time_range';
      }

      return invalidMessage;
    }

    isTimeRangeValid = ({timeSlot, modifiedTime, index, timeType}, isTimeSlotUpdated = false) => {
      const updatedTimeSlot = isTimeSlotUpdated ? {...timeSlot} : {
        startTime: (this.state.partiallyFilledTimeSlots[timeType][index] && this.state.partiallyFilledTimeSlots[timeType][index].startTime) || timeSlot.startTime,
        endTime: (this.state.partiallyFilledTimeSlots[timeType][index] && this.state.partiallyFilledTimeSlots[timeType][index].endTime) || timeSlot.endTime,
      };

      return isTimeIn24HourRange(updatedTimeSlot[modifiedTime]) &&
      isValidRangeTimeString(updatedTimeSlot[modifiedTime]) &&
      isEndTimeAfterStartTime(updatedTimeSlot.startTime, updatedTimeSlot.endTime);
    }

    /****** Controller behavior (pure input, ticker, dropdown) ******/
    getControllerOptions = () => {
      const options = [
        {
          value: 'text',
          label: 'time_input_settings_input_option_text_only_label',
          illustration: <Symbol style={{height: 72}} name="timepicker-text"/>
        },
        {
          value: 'stepper',
          label: 'time_input_settings_input_option_stepper_label',
          illustration: <Symbol style={{height: 72}} name="timepicker-stepper"/>
        },
        {
          value: 'dropdown',
          label: 'time_input_settings_input_option_dropdown_label',
          illustration: <Symbol style={{height: 72}} name="timepicker-dropdown"/>
        }
      ];
      return options;
    }

    onControllerChange = value => {
      const keyArr = ['controller'];
      const valObj = {controller: value};
      if (this.props.compProperties.controller === 'dropdown' && value !== 'dropdown') {
        keyArr.push('initialTime');
        valObj.initialTime = 'current';
      }
      this.props.updateProperties(keyArr, valObj);
    }

    /****** Initial time (none, current time, set specific time, or placeholder if a dropdown is chosen) ******/
    getInitialTimeOptions = () => {
      const timeOptions = [];
      if (this.shouldRenderInitialTextNothingSection()) {
        timeOptions.push({
          value: 'none',
          label: 'time_input_settings_show_text_nothing_option'
        });
      }
      if (this.props.compProperties.controller === 'dropdown' && this.shouldRenderInitialTextPlaceholderSection()) {
        timeOptions.push({
          value: 'placeholder',
          label: 'time_input_settings_show_text_placeholder_option'
        });
      }
      if (this.shouldRenderInitialTextCurrentTimeSection()) {
        timeOptions.push({
          value: 'current',
          label: 'time_input_settings_show_text_current_time_option'
        });
      }
      if (this.shouldRenderInitialTextSpecificTimeSection()) {
        timeOptions.push({
          value: 'value',
          label: 'time_input_settings_show_text_specific_time_option'
        });
      }

      return timeOptions;
    }

    onChangeInitialTime = value => {
      this.props.bi.event(biEvents.changeValue, {
        inputType: 'timePicker',
        field_type: 'radio',
        field_name: 'initial text',
        previousValue: this.props.compProperties.initialTime,
        value,
      });

      this.props.updateProperties('initialTime', value);
      if (value === 'value') {
        this.props.updateData('value', '14:30:00.000');
      } else {
        this.props.updateData('value', '');
      }
    }

    onChangePlaceHolderText = value => {
      this.props.updateData('placeholder', value);
    }

    onChangeSpecificTime = value => {
      this.props.updateData('value', formatValue(value));
    }

    updateProp = (propName, val) => {
      this.props.updateProperties(propName, val);
    }

    updateData = (attrName, val) => {
      this.props.updateData(attrName, val);
    }

    /****** Time increment ******/
    onIncrementChange = value => {
      this.props.bi.event(biEvents.changeValue, {
        inputType: 'timePicker',
        field_type: 'number',
        field_name: 'time interval',
        previousValue: this.props.compData.step,
        value,
      });

      this.props.updateData('step', value);
    }

    /****** General settings (read only, required) ******/
    handleReadOnlyChange = value => {
      this.props.updateProperties('readOnly', value);
    }

    handleRequiredChange = value => {
      this.props.updateProperties('required', value);
    }

    onValueKeyDown = e => {
      const {value} = this.props.compData;

      if (!TimePickerUtils.isValidTime(value, false)) {
        return;
      }

      if (e.key === 'ArrowUp') {
        const newValue = TimePickerUtils.increment({value, field: 2, step: 15});
        this.props.updateData('value', formatValue(newValue));
      } else if (e.key === 'ArrowDown') {
        const newValue = TimePickerUtils.decrement({value, field: 2, step: 15});
        this.props.updateData('value', formatValue(newValue));
      }
    }

    timeSlotsData = {
      enabledTimes: {
        getAllTimeSlots: () => [...this.getEnabledTimes()],
        getFilledTimeSlots: () => [...this.props.compProperties.enabledTimes],
        getPartiallyFilledTimeSlots: () => this.state.partiallyFilledTimeSlots.enabledTimes,
      },
      disabledTimes: {
        getAllTimeSlots: () => [...this.getDisabledTimes()],
        getFilledTimeSlots: () => [...this.props.compProperties.disabledTimes],
        getPartiallyFilledTimeSlots: () => this.state.partiallyFilledTimeSlots.disabledTimes,
      }
    }

    onTimeSlotChange = ({value, index, didChangeStartTime, timeType}) => {
      this.setState({
        lastModifiedTimeRange: {
          ...this.state.lastModifiedTimeRange,
          [timeType]: {
            ...this.state.lastModifiedTimeRange[timeType],
            [index]: {
              modifiedTime: didChangeStartTime ? 'startTime' : 'endTime',
            }
          }
        }
      });

      const timeSlotArr = this.timeSlotsData[timeType].getAllTimeSlots();
      const partiallyFilledTimeSlots = {
        ...this.timeSlotsData[timeType].getPartiallyFilledTimeSlots(),
      };
      const partiallyFilledStartTime = this.state.partiallyFilledTimeSlots[timeType][index] &&
        this.state.partiallyFilledTimeSlots[timeType][index].startTime;
      const partiallyFilledEndTime = this.state.partiallyFilledTimeSlots[timeType][index] &&
        this.state.partiallyFilledTimeSlots[timeType][index].endTime;

      const newTimeSlot = {
        startTime: didChangeStartTime ? value : partiallyFilledStartTime || timeSlotArr[index].startTime,
        endTime: didChangeStartTime ? partiallyFilledEndTime || timeSlotArr[index].endTime : value,
      };

      const isTimeRangeValid = this.isTimeRangeValid({timeSlot: newTimeSlot, modifiedTime: didChangeStartTime ? 'startTime' : 'endTime', index, timeType}, true) &&
                               this.isTimeRangeValid({timeSlot: newTimeSlot, modifiedTime: didChangeStartTime ? 'endTime' : 'startTime', index, timeType}, false);
      const isUpdatingExistingTimeSlot = timeSlotArr[index].startTime && timeSlotArr[index].endTime;
      const isDoneCreatingNewTimeSlot = newTimeSlot.startTime && newTimeSlot.endTime;

      const partiallyFilledSlotsWithNewTimeSlot = {partiallyFilledTimeSlots: {
        ...this.state.partiallyFilledTimeSlots,
        [timeType]: {
          ...partiallyFilledTimeSlots,
          [index]: newTimeSlot}
      }};
      if (isUpdatingExistingTimeSlot) {
        if (isTimeRangeValid) {
          const timeSlots = [...this.props.compProperties[timeType]];
          timeSlots.splice(index, 1, newTimeSlot);

          this.props.updateProperties(timeType, timeSlots);

          delete partiallyFilledTimeSlots[index];

          this.setState({
            partiallyFilledTimeSlots: {
              ...this.state.partiallyFilledTimeSlots,
              [timeType]: partiallyFilledTimeSlots},
          });

        } else {
          this.setState(partiallyFilledSlotsWithNewTimeSlot);
        }
      } else if (isDoneCreatingNewTimeSlot) {
        if (isTimeRangeValid) {
          delete partiallyFilledTimeSlots[index];

          this.setState({
            partiallyFilledTimeSlots: {
              ...this.state.partiallyFilledTimeSlots,
              [timeType]: partiallyFilledTimeSlots},
          });

          this.props.updateProperties(timeType, [...this.props.compProperties[timeType], newTimeSlot]);
        } else {
          this.setState(partiallyFilledSlotsWithNewTimeSlot);
        }

      } else {
        this.setState(partiallyFilledSlotsWithNewTimeSlot);
      }
    }

    onAddEmptyTimeSlot = ({timeType}) => {
      const newSlotIndex = this.timeSlotsData[timeType].getAllTimeSlots().length;
      const availabilityType = timeType === 'enabledTimes' ? 'available' : 'unavailable';

      this.props.bi.event(biEvents.changeValue, {
        inputType: 'timePicker',
        button_name: `add ${availabilityType} times`,
        field_name: `${availabilityType} times`
      });

      this.setState({
        partiallyFilledTimeSlots: {
          ...this.state.partiallyFilledTimeSlots,
          [timeType]: {
            ...this.state.partiallyFilledTimeSlots[timeType],
            [newSlotIndex]: {
              startTime: '',
              endTime: '',
            }
          }
        },
      });
    }

    onDeleteTimeSlot = ({index, timeType}) => {
      const partiallyFilledTimeSlots = {
        ...this.timeSlotsData[timeType].getPartiallyFilledTimeSlots()
      };

      if (index >= this.props.compProperties[timeType].length) {
        delete partiallyFilledTimeSlots[index];

        const updatedPartiallyFilledTimeSlots = Object.keys(
          partiallyFilledTimeSlots,
        ).reduce((previousValue, currentValue) => {
          const newKey = currentValue > index ? currentValue - 1 : currentValue;

          return Object.assign(previousValue, {
            [newKey]: partiallyFilledTimeSlots[currentValue],
          });
        }, {});

        this.setState({
          partiallyFilledTimeSlots: {
            ...this.state.partiallyFilledTimeSlots,
            [timeType]: updatedPartiallyFilledTimeSlots
          }
        });
      } else {
        const timeSlots = this.timeSlotsData[timeType].getFilledTimeSlots();
        timeSlots.splice(index, 1);

        this.updateProp(timeType, timeSlots);
      }
    }
    renderEmptyLabelTooltipContent() {
      const {translate} = this.props;
      return (
        <RichText>
          {translate('time_input_settings_label_empty_warning')}{' '}
          <a
            onClick={() => {
              this.props.openHelpArticle('0929a4d2-1fbd-45b8-a064-4e6ceb6a20df');
            }}
          >
            {translate('time_input_settings_label_empty_warning_link')}
          </a>
        </RichText>
      );
    }

    onTimeSlotsLearnMoreClick(timeType) {
      const helpId =
        timeType === 'enabledTimes' ?
          '1fcd2694-b8d1-4d24-9d43-ec8ee0b4528c' :
          'd44b8704-f13a-444d-998f-a24b3cc0d73b';

      this.props.openHelpArticle(helpId);
    }

    render() {
      const placeholderSelected = this.props.compProperties.initialTime === 'placeholder';
      const specificSelected = this.props.compProperties.initialTime === 'value';
      const showLabel = this.state.labelToggle;

      return (
        <div>
          {this.shouldRenderLabelSection() &&
          <div>
            {!this.shouldRemoveLabelToggle() &&
              <div>
                <ToggleSwitch
                  data-hook="title-toggle"
                  label="time_input_settings_show_title_toggle"
                  value={showLabel}
                  onChange={this.handleLabelToggleChanged}/>

                <Divider long={!showLabel}/>
              </div>
            }

            {(showLabel || this.shouldRemoveLabelToggle()) &&
              <div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '14px 12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginLeft: '12px',
                    }}
                  >
                    <TextLabel value="time_input_settings_field_title_label"/>
                    {this.shouldRemoveLabelToggle() &&
                      !this.props.compData.label ? (
                        <Tooltip
                          alignment="TOP"
                          content={this.renderEmptyLabelTooltipContent()}
                          interactive
                        >
                          <div data-hook="time-picker-empty-label-warning">
                            <Symbol name="warning" style={{cursor: 'pointer'}}/>
                          </div>
                        </Tooltip>
                      ) : null}
                  </div>
                  <TextInput
                    data-hook="title-input"
                    isValid
                    hideSuccessIndication
                    maxLength={300}
                    placeholder="ratings_write_settings_onhover_placeholder"
                    value={this.state.currentLabel}
                    onChange={val => this.handleLabelChanged(val)}/>
                </div>
                <Divider long/>
              </div>
            }
          </div>
          }
          {this.shouldRenderAmPmFormatSection() &&
            <div>
              <Composites.DropDownLabeled>
                <InfoIcon text="time_input_settings_time_format_tooltip"/>
                <TextLabel value="time_input_settings_time_format_label"/>
                <DropDown
                  data-hook="useAmPmFormat"
                  options={TIME_FORMATS}
                  value={this.props.compProperties.useAmPmFormat}
                  onChange={this.onUseAmPmFormatChange}/>
              </Composites.DropDownLabeled>
              <Divider long/>
            </div>
          }
          {this.shouldRenderInputOptionSection() &&
          <div>
            <Composites.Thumbnails>
              <InfoIcon text="time_input_settings_input_option_tooltip"/>
              <TextLabel value="time_input_settings_input_option_label"/>
              <Thumbnails
                data-hook="controller"
                maxThumbsPerRow={3}
                value={this.props.compProperties.controller}
                onChange={this.onControllerChange}
                options={this.getControllerOptions()}/>
            </Composites.Thumbnails>
            <Divider long/>
          </div>
          }
          {this.shouldRenderInitialTimeSection() &&
          <div>
            <Composites.RadioButtonsLabeled>
              <TextLabel value="time_input_settings_show_text_label"/>
              <RadioButtons
                data-hook="initialTime"
                value={this.props.compProperties.initialTime}
                onChange={this.onChangeInitialTime}
                options={this.getInitialTimeOptions()}/>
            </Composites.RadioButtonsLabeled>

            {(placeholderSelected || specificSelected) &&
            <Divider/>
            }

            {placeholderSelected &&
            <Composites.TextInputLabeled>
              <TextLabel value="time_input_settings_placeholder_text_label"/>
              <TextInput
                data-hook="placeholder"
                onChange={this.onChangePlaceHolderText}
                value={this.props.compData.placeholder}
                placeholder="time_input_settings_placeholder_text"/>
            </Composites.TextInputLabeled>
            }

            {specificSelected &&
            <Composites.TextInputLabeled>
              <TextLabel value="time_input_settings_set_specific_time_label"/>
              <TextInput
                data-hook="value"
                onChange={this.onChangeSpecificTime}
                value={timeToDisplay(this.props.compData.value)}
                invalidMessage="time_input_settings_invalid_time_error"
                onKeyDown={this.onValueKeyDown}
                validator={timeValidator}/>
            </Composites.TextInputLabeled>
            }

            <Divider long/>
          </div>
          }
          {this.shouldRenderStepSection() &&
          <div>
            <Composites.SliderLabeled>
              <InfoIcon text="time_input_settings_increments_tooltip"/>
              <TextLabel value="time_input_settings_increments_label"/>
              <Slider
                data-hook="step"
                min={1}
                max={60}
                value={this.props.compData.step}
                onChange={this.onIncrementChange}/>
            </Composites.SliderLabeled>
            <Divider long/>
          </div>
          }

          {
            this.shouldRenderTimeSlotsSection() &&
              ['enabledTimes', 'disabledTimes'].map(timeType => {
                const timeSlotsArr = timeType === 'enabledTimes' ? this.getEnabledTimes() : this.getDisabledTimes();
                const translationKey = timeType === 'enabledTimes' ? 'enabled' : 'disabled';

                return (
                  <div data-hook={timeType} key={timeType}>
                    <Divider long/>
                    <Composites.SectionDividerWithInfoIcon>
                      <InfoIcon
                        dataHook={`${timeType}-title-info-icon`}
                        text={`time_input_settings_${translationKey}_times_label_tooltip`}
                        linkText={`time_input_settings_${translationKey}_times_label_tooltip_learn_more`}
                        onLinkClick={() =>
                          this.onTimeSlotsLearnMoreClick(timeType)
                        }
                      />
                      <SectionDivider>
                        <TextLabel dataHook={`${timeType}-title`} value={`time_input_settings_${translationKey}_times_label`}/>
                      </SectionDivider>
                    </Composites.SectionDividerWithInfoIcon>
                    <Divider long/>
                    {
                      timeSlotsArr.map((timeSlot, index) => {
                        const shouldRenderLabel = index === 0;

                        return (
                          <Composites.TimeRange key={index} data-hook={`${timeType}-wrapper`}>
                            {shouldRenderLabel && <Text dataHook={`${timeType}-from-label`} type="secondary">{`time_input_settings_${translationKey}_times_input_title_from`}</Text>}
                            <TextInput
                              onChange={value => this.onTimeSlotChange({value, index, didChangeStartTime: true, timeType})}
                              value={timeToDisplay(timeSlot.startTime)}
                              isValid={this.isTimeRangeValid({timeSlot, modifiedTime: 'startTime', index, timeType})}
                              invalidMessage={this.getTimeRangeInvalidMessage(timeSlot, 'startTime', index, timeType)}
                              data-hook={`${timeType}-start`}
                              validateInitialValue={false}
                              placeholder={`time_input_settings_${translationKey}_times_input_title_from_placeholder`} />
                            {shouldRenderLabel && <Text dataHook={`${timeType}-to-label`} type="secondary">{`time_input_settings_${translationKey}_times_input_title_to`}</Text>}
                            <TextInput
                              onChange={value => this.onTimeSlotChange({value, index, didChangeStartTime: false, timeType})}
                              value={timeToDisplay(timeSlot.endTime)}
                              isValid={this.isTimeRangeValid({timeSlot, modifiedTime: 'endTime', index, timeType})}
                              invalidMessage={this.getTimeRangeInvalidMessage(timeSlot, 'endTime', index, timeType)}
                              data-hook={`${timeType}-end`}
                              validateInitialValue={false}
                              placeholder={`time_input_settings_${translationKey}_times_input_title_to_placeholder`} />
                            <IconButton
                              skin="light"
                              dataHook={`${timeType}-delete-button`}
                              onClick={() => this.onDeleteTimeSlot(({index, timeType}))}>
                              <Delete/>
                            </IconButton>
                          </Composites.TimeRange>
                        );
                      })
                    }
                    {
                      <Composites.ButtonLeft>
                        <TextButton
                          prefixIcon={<Add />}
                          size="small"
                          dataHook={`${timeType}-add-button`}
                          disabled={timeSlotsArr.length >= 5}
                          onClick={() => this.onAddEmptyTimeSlot({timeType})}
                        >
                          {`time_input_settings_${translationKey}_times_add`}
                        </TextButton>
                      </Composites.ButtonLeft>
                    }
                    <Divider long/>
                  </div>

                );
              })
          }

          {this.shouldRenderGeneralSettings() &&
          <Composites.CheckboxesLabeled>
            <InfoIcon text={this.getGeneralSettingsTooltipContent()}/>
            <TextLabel value="Input_Controls_General_Settings_Label" data-hook="general-settings"/>
            {this.shouldRenderRequiredSection() &&
            <Checkbox
              data-hook="required"
              label="Input_Controls_General_Settings_Required_Checkbox"
              value={!!this.props.compProperties.required}
              onChange={this.handleRequiredChange}
              labelAfterSymbol/>
            }
            {this.shouldRenderReadOnlySection() &&
            <Checkbox
              data-hook="readOnly"
              label="Input_Controls_General_Settings_Read_Only_Checkbox"
              value={!!this.props.compProperties.readOnly}
              onChange={this.handleReadOnlyChange}
              labelAfterSymbol/>
            }
          </Composites.CheckboxesLabeled>
          }
        </div>
      );
    }
}

const mapDispatchToProps = dispatch => ({
  openHelpArticle: id =>
    dispatch((_dispatch, _getState, {editorAPI}) =>
      editorAPI.panelManager.openHelpCenter(id),
    ),
});

const ConnectedPanel = hoc.connect(hoc.STORES.EDITOR_API, null, mapDispatchToProps)(TimePickerSettingsPanel);
ConnectedPanel.pure = TimePickerSettingsPanel;

export const timePickerSettingsPanelDef = {
  PanelClass: ConnectedPanel,
  title: 'time_input_settings_title',
  helpId: 'e5b32278-325f-43bb-bada-047c6724b0fe',
  experiments: ['exptimepicker', 'se_InputElementsRemoveLabelToggles', 'se_TimePickerDisabledTimes']
};
